import BaseUI from "../base-ui.ts";
import LogoFragment from "../fragments/logo.ts";
import LobbyFragment from "../fragments/lobby.ts";
import State from "../../state/state.ts";
import EatOutApiClient from "../../api-client/eat-out-api-client.ts";
import V1GroupJoined from "../../payloads/incoming/v1-group-joined.ts";
import UiUtils from "../../utils/ui-utils.ts";
import Route from "../../enums/route.ts";
import LogUtils from "../../utils/log-utils.ts";
import V1GroupReadied from "../../payloads/incoming/v1-group-readied.ts";
import Coordinates from "../../models/coordinates.ts";
import Group from "../../models/group.ts";
import V1UserGot from "../../payloads/incoming/v1-user-got.ts";
import SharedApiLogic from "../../api-client/shared-api-logic.ts";
import V1GroupRestaurantCategoriesChosen from "../../payloads/incoming/v1-group-restaurant-categories-chosen.ts";
import StateUtils from "../../state/state-utils.ts";
import CACHE_BUSTER from "../../../../../cache-buster.json" with { type: "json" };

export default class CreateGroupLobbyScreen extends BaseUI {
  private readonly lobby: LobbyFragment;
  private coordinates: Coordinates | null;
  private readonly readyButton: HTMLButtonElement;

  constructor(state: State, apiClient: EatOutApiClient, navigateCallback: (route: Route) => void) {
    super("create-group-screen", state, apiClient, navigateCallback);

    // add UI elements
    this.lobby = new LobbyFragment(this.state, this.apiClient, this.navigateCallback);

    // subscribe to events
    this.apiClient.v1GroupJoinedCallback = this.handleV1GroupJoined.bind(this);
    this.apiClient.v1GroupReadiedCallback = this.handleV1GroupReadied.bind(this);
    this.apiClient.v1UserGotCallback = this.handleV1UserGot.bind(this);
    this.apiClient.v1GroupRestaurantCategoriesChosenCallback = this.handleV1GroupRestaurantCategoriesChosenCallback
      .bind(this);

    // fill lobby
    this.fillLobbyWithExistingGroupMembers();

    // geolocation
    this.coordinates = null;
    this.setupGeolocationRetrieval();

    // ready button (enabled/disabled based on resolving geolocation)
    this.readyButton = UiUtils.button("Ready!");
    this.readyButton.disabled = true;
  }

  public override render(newElements: HTMLElement[] = []): HTMLDivElement {
    // logo
    const logo: LogoFragment = new LogoFragment(this.state, this.apiClient, this.navigateCallback);
    newElements.push(logo.render());

    // form
    const form: HTMLFormElement = UiUtils.form(this.handleFormSubmitEvent.bind(this));
    newElements.push(form);

    // group code
    const codeLabelParagraph: HTMLParagraphElement = UiUtils.paragraph("Share your group code ");
    form.appendChild(codeLabelParagraph);
    const groupCode: string = StateUtils.getCurrentGroupCode(this.state);
    const codeParagraph: HTMLParagraphElement = UiUtils.paragraph(groupCode);
    form.appendChild(codeParagraph);

    // export/share button
    const exportShareButton: HTMLButtonElement = UiUtils.button(
      "",
      async () => await this.handleExportShareGroupCode(),
    );
    exportShareButton.type = "button";
    exportShareButton.classList.add("export-share-button");
    const exportShareImage: HTMLImageElement = UiUtils.image(CACHE_BUSTER["static/image/icon/export-share.svg"]);
    exportShareButton.appendChild(exportShareImage);
    form.appendChild(exportShareButton);

    // radius slider
    const radiusMin = 5;
    const radiusMax = 25;
    const radiusStep = 5;
    const radiusDefaultValue = 10;
    const radiusLabel: HTMLLabelElement = UiUtils.label("Radius (miles): ");
    const radiusSlider: HTMLInputElement = UiUtils.slider(
      "radius",
      radiusMin,
      radiusMax,
      radiusStep,
      radiusDefaultValue,
      "radius-labels",
    );
    radiusLabel.appendChild(radiusSlider);
    form.appendChild(radiusLabel);

    // radius slider labels
    const radiusDatalist: HTMLDataListElement = UiUtils.datalist("radius-labels");
    radiusDatalist.classList.add("labelled-datalist");
    radiusLabel.appendChild(radiusDatalist);
    for (let radius = radiusMin; radius <= radiusMax; radius += radiusStep) {
      const radiusDatalistOption: HTMLOptionElement = UiUtils.datalistOption(String(radius), String(radius));
      radiusDatalist.appendChild(radiusDatalistOption);
    }

    // group members
    const groupMembersLabelParagraph: HTMLParagraphElement = UiUtils.paragraph("Group Members");
    form.appendChild(groupMembersLabelParagraph);
    form.appendChild(this.lobby.render());

    // ready button
    form.appendChild(this.readyButton);

    // back button
    const backButton: HTMLButtonElement = UiUtils.button(
      "Back",
      () => this.navigateCallback(Route.MAIN_MENU),
    );
    newElements.push(backButton);

    return super.render(newElements);
  }

  private async handleExportShareGroupCode(): Promise<void> {
    // check to see if Web Share API is supported on the user's device
    if (!navigator.canShare || !navigator.share) {
      LogUtils.error("Sharing is not supported on this device.", "Sharing is not supported on this device.");
    }

    // create Web Share API data
    const groupCode: string = StateUtils.getCurrentGroupCode(this.state);
    const url = `https://www.eatoutnear.me/app?groupcode=${groupCode}`;
    const data = {
      title: "Eat Out group code",
      text: `Your Eat Out group code is ${groupCode}.\n\n${url}`,
      url: url,
    };

    // make sure the client can handle Web Share API (and the data I'm trying to share)
    if (!navigator.canShare(data)) {
      LogUtils.error(
        "Sharing is not supported on this device.",
        "Sharing is not supported on this device.",
      );
    }

    // share the data
    try {
      await navigator.share(data);
    } catch (error) {
      LogUtils.error(error, "Failed to share group code.");
    }
  }

  private setupGeolocationRetrieval(): void {
    // make sure the Geolocation API is available
    if (!("geolocation" in navigator)) {
      LogUtils.error(
        "Geolocation API is unavailable on this device.",
        "Geolocation API is unavailable on this device.",
      );
    }

    // geolocation options
    const options = {
      maximumAge: 1000 * 60 * 60, // (1 hour) max age in milliseconds of cached position that is acceptable to return
      timeout: 1000 * 15, // (15 seconds) allowed time in milliseconds to return a position
      enableHighAccuracy: true, // can result in slower response times or increased power consumption
    };

    // get geolocation
    navigator.geolocation.getCurrentPosition(
      (position: GeolocationPosition) => this.handleGeolocationSuccess(position),
      (error: GeolocationPositionError) => this.handleGeolocationFailure(error),
      options,
    );
  }

  private handleGeolocationSuccess(position: GeolocationPosition): void {
    // save coordinates so that the "group is ready" payload can be sent
    this.coordinates = new Coordinates(position.coords.latitude, position.coords.longitude);
    LogUtils.debug(
      this.debug,
      `Resolved geolocation coordinates: latitude='${this.coordinates.latitude}', longitude='${this.coordinates.longitude}', accuracy='${position.coords.accuracy}' (meters; 95% confidence level)`,
    );

    // re-enable the "Ready" button
    this.readyButton.disabled = false;
  }

  private handleGeolocationFailure(error: GeolocationPositionError): void {
    let humanReadableErrorMessage: string;
    switch (error.code) {
      case error.PERMISSION_DENIED: {
        humanReadableErrorMessage = "Permission to resolve geolocation is denied.";
        break;
      }
      case error.POSITION_UNAVAILABLE: {
        humanReadableErrorMessage = "Geolocation position is unavailable.";
        break;
      }
      case error.TIMEOUT: {
        humanReadableErrorMessage = "Timed out while resolving geolocation.";
        break;
      }
      default: {
        humanReadableErrorMessage = "Encountered unexpected geolocation error.";
        break;
      }
    }
    LogUtils.error(`Failed to resolve latitude/longitude: ${error.message}`, humanReadableErrorMessage);
  }

  private handleFormSubmitEvent(event: SubmitEvent): void {
    // make sure the event's target was truly an HTMLFormElement
    const eventTarget: EventTarget | null = event.target;
    if (!(eventTarget instanceof HTMLFormElement)) {
      LogUtils.error(
        `attempted to parse form submit event, but the event's target was a ${typeof eventTarget} when we expected an HTMLFormElement`,
      );
    }

    // get radius from form
    const formData: FormData = new FormData(eventTarget);
    if (!formData.has("radius")) {
      LogUtils.error("attempted to get radius from Create Group form, but it doesn't exist");
    }
    const radiusMiles: FormDataEntryValue | null = formData.get("radius");
    if (typeof radiusMiles !== "string") {
      LogUtils.error(
        `attempted to retrieve radius from Create Group form, but it was a '${typeof radiusMiles}' when we expected a 'string'`,
      );
    }

    // convert radius from miles to meters
    // (round to nearest meter)
    const metersPerMile = 1609.34;
    const radiusMeters: number = Math.floor(Number(radiusMiles) * metersPerMile);

    // make sure geolocation coordinates have been discovered
    if (this.coordinates === null) {
      LogUtils.error("Geolocation coordinates have not been discovered yet.", "Failed to resolve latitude/longitude.");
    }

    // attempt readying up the group
    this.apiClient.sendReadyUpGroupPayload(
      this.coordinates,
      radiusMeters,
    );
  }

  private handleV1GroupJoined(v1GroupJoined: V1GroupJoined): void {
    SharedApiLogic.handleV1GroupJoined(this.state, v1GroupJoined);

    // retrieve the user's account, for display within the lobby fragment
    this.apiClient.sendGetUserPayload(v1GroupJoined.payload.user.id);
  }

  private handleV1UserGot(v1UserGot: V1UserGot): void {
    this.lobby.addUser(v1UserGot.payload.user);
  }

  private handleV1GroupReadied(v1GroupReadied: V1GroupReadied): void {
    SharedApiLogic.handleV1GroupReadied(this.state, v1GroupReadied);

    // now that we've joined the group, switch to restaurant categories screen
    LogUtils.debug(this.debug, "Successfully readied up the group.");
    this.navigateCallback(Route.RESTAURANT_CATEGORIES);
  }

  private handleV1GroupRestaurantCategoriesChosenCallback(
    v1GroupRestaurantCategoriesChosen: V1GroupRestaurantCategoriesChosen,
  ): void {
    SharedApiLogic.handleV1GroupRestaurantCategoriesChosenCallback(
      this.debug,
      this.state,
      v1GroupRestaurantCategoriesChosen,
    );
  }

  private fillLobbyWithExistingGroupMembers(): void {
    const group: Group = StateUtils.getCurrentGroup(this.state);

    // there should only be one member in the group - the group creator
    if (group.members.size !== 1) {
      LogUtils.error(
        "attempted to render RestaurantCategoriesLobby screen, " +
          "but the 'current group' already has more than 1 member (the creator) in it",
      );
    }

    // put all the members of the group into the Lobby
    for (const [userId, _memberState] of group.members) {
      this.apiClient.sendGetUserPayload(userId);
    }
  }
}
